const API_BASE = process.env.REACT_APP_API_URL || 'https://api.grandindigoembroidery.com';

const headers = () => ({
  'Content-Type': 'application/json',
  'Accept': 'application/json',
});

// ─── Customers ────────────────────────────────────────────────────────────────

export const fetchAllCustomers = async () => {
  const res = await fetch(`${API_BASE}/payment/customers`, { headers: headers() });
  if (!res.ok) throw new Error('Failed to fetch customers');
  return res.json();
};

// ─── Orders by email ──────────────────────────────────────────────────────────
/**
 * Fetch a user's orders by their email address.
 * Returns an array of orders if the user exists.
 * Throws if user not found (404) — caller handles this to detect new user.
 */
export const fetchUserOrdersByEmail = async (email) => {
  const res = await fetch(
    `${API_BASE}/payment/orders-by-email?email=${encodeURIComponent(email)}`,
    { headers: headers() }
  );
  if (res.status === 404) {
    throw new Error('User not found');
  }
  if (!res.ok) throw new Error('Failed to fetch orders');
  return res.json(); // array of { id, order_number, service, status }
};

// ─── Fetch payment by token (for shareable link) ─────────────────────────────
/**
 * Called when the customer opens their unique payment link.
 * Returns { clientSecret, orderId, orderDetails } or throws.
 */
export const fetchPaymentByToken = async (token) => {
  const res = await fetch(`${API_BASE}/payment/pay/${token}`, { headers: headers() });
  const data = await res.json();
  if (res.status === 410) {
    const err = new Error(data.message || 'Already paid');
    err.alreadyPaid = true;
    throw err;
  }
  if (!res.ok) throw new Error(data.message || 'Payment link not found.');
  return data; // { clientSecret, orderId, orderDetails }
};

// ─── Payment Intent ───────────────────────────────────────────────────────────
/**
 * Create a Stripe PaymentIntent and optionally create a new user.
 * @param {Object} payload
 * @param {string|null} payload.userId          – null when new customer
 * @param {string} payload.customerName
 * @param {string} payload.customerEmail
 * @param {string} payload.customerPhone
 * @param {string} payload.packageService
 * @param {string} payload.packageDetails
 * @param {number} payload.packagePrice         – dollars e.g. 249.00
 * @param {string} payload.orderNumber
 * @param {string|null} payload.linkedOrderId   – orders.id if linked to existing order
 */
export const createPaymentIntent = async (payload) => {
  const res = await fetch(`${API_BASE}/payment/create-intent`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || 'Failed to create payment intent');
  return data; // { clientSecret, orderId, userId, isNewUser }
};

/**
 * Confirm a successful payment on the server side.
 */
export const confirmPayment = async (payload) => {
  const res = await fetch(`${API_BASE}/payment/confirm`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || 'Failed to confirm payment');
  return data;
};